import { useEffect, useMemo, useState } from 'react';
import { authClient } from '../../utils/amplifyClient';
import {
    formatIncompleteItemLine,
    incompleteScheduledItems,
    normalizeRecipientEmails,
    type ScheduledCompletionItem,
} from '../../utils/pmIncompleteItems';
import { Btn } from '../ui';
import '../../css/PMInspectionProcess.css';

interface PMIncompleteItemsSectionProps {
    items: ScheduledCompletionItem[];
    unitName: string;
    serviceLevelName?: string | null;
    serviceDate?: string | null;
    pmConfigurationId?: string | null;
}

export const PMIncompleteItemsSection = ({
    items,
    unitName,
    serviceLevelName,
    serviceDate,
    pmConfigurationId,
}: PMIncompleteItemsSectionProps) => {
    const incomplete = useMemo(() => incompleteScheduledItems(items), [items]);
    const lines = useMemo(() => incomplete.map(formatIncompleteItemLine), [incomplete]);
    const [recipients, setRecipients] = useState<string[]>([]);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState('');
    const [messageIsError, setMessageIsError] = useState(false);

    useEffect(() => {
        if (!pmConfigurationId || lines.length === 0) {
            setRecipients([]);
            return;
        }
        let isCancelled = false;
        setLoadingRecipients(true);
        (async () => {
            try {
                const { data } = await authClient.models.PMNotificationSetting.listPMNotificationSettingByPmConfigurationId({
                    pmConfigurationId,
                });
                if (isCancelled) return;
                const emails = normalizeRecipientEmails(
                    (data ?? []).flatMap((setting) => setting?.recipients ?? []),
                );
                setRecipients(emails);
            } catch (err) {
                if (isCancelled) return;
                console.error('Failed to load PM email recipients:', err);
                setRecipients([]);
            } finally {
                if (!isCancelled) setLoadingRecipients(false);
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [pmConfigurationId, lines.length]);

    const handleEmail = async () => {
        setSending(true);
        setMessage('');
        setMessageIsError(false);
        try {
            const result = await authClient.mutations.sendPMIncompleteItemsEmail({
                unitName,
                serviceLevelName: serviceLevelName || undefined,
                serviceDate: serviceDate || undefined,
                items: lines,
                recipients,
            });
            const raw = result.data;
            const payload = (typeof raw === 'string' ? JSON.parse(raw) : raw) as {
                error?: string;
                recipientCount?: number;
            } | null;
            if (payload && typeof payload === 'object' && payload.error) {
                setMessageIsError(true);
                setMessage(String(payload.error));
                return;
            }
            const count = Number(payload && typeof payload === 'object' ? payload.recipientCount : 0) || recipients.length;
            setMessage(`Sent to ${count} recipient${count === 1 ? '' : 's'}.`);
        } catch (err) {
            console.error('Failed to email incomplete PM items:', err);
            setMessageIsError(true);
            setMessage('The not-completed email could not be sent.');
        } finally {
            setSending(false);
        }
    };

    if (items.length === 0) return null;

    return (
        <div className="pm-incomplete-section">
            <h3 className="pm-incomplete-title">Not completed</h3>
            {lines.length === 0 ? (
                <p className="pm-incomplete-hint">Every scheduled item and procedure was marked done.</p>
            ) : (
                <>
                    <p className="pm-incomplete-hint">
                        These scheduled items and procedures were left unchecked. This is separate from the due-soon reminder.
                    </p>
                    <ul className="pm-incomplete-list">
                        {lines.map((line) => (
                            <li key={line}>{line}</li>
                        ))}
                    </ul>
                    <Btn
                        variant="secondary-outlined"
                        buttonSize="sm"
                        buttonText={sending ? 'Sending…' : 'Email not completed'}
                        buttonOnClick={handleEmail}
                        buttonDisabled={sending || loadingRecipients || recipients.length === 0}
                    />
                    {recipients.length === 0 && !loadingRecipients && (
                        <p className="pm-incomplete-hint">
                            Add at least one recipient on this unit’s Preventive Maintenance email settings before sending.
                        </p>
                    )}
                    {message && (
                        <p className={messageIsError ? 'pm-incomplete-error' : 'pm-incomplete-hint'} role="status">
                            {message}
                        </p>
                    )}
                </>
            )}
        </div>
    );
};
